import { createLogger } from "@/logger";
import { formatItemDate } from "./feedItemDateGrouping";

const log = createLogger("feed");

import { useLiveQuery } from "@tanstack/react-db";
import {
  createContext,
  type MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { serverNow } from "@storyboard/web-client";
import type { VirtualListHandle } from "@/components/VirtualizedList";
import { useDispatchEnabled } from "@/hooks/dispatch/useDispatchEnabled";
import { useAccountLookup } from "@/hooks/workspace/useAccountLookup";
import { DataContext } from "@/models/DataProvider";
import { type FeedRow, feedEventMembershipId, interleaveFeedRows } from "@/models/feed/feedRows";
import { tmsEventsForMembershipQuery } from "@/models/repositories/TmsEvent";
import type {
  AccountEvent,
  AccountWithWsMembershipAvailability,
  AudioEncoding,
  CallRecord,
  DisplayArtifact,
  Feed,
  File,
  Item,
  Link,
  TmsEventRow,
  Transcription,
} from "@/store/types";
import type { SetStateType } from "@/utils";
import {
  audioEncodingsQuery,
  callRecordsQuery,
  displayArtifactsQuery,
  eventsForFeedItemsQuery,
  feedWindowAnchorQuery,
  filesQuery,
  itemsQuery,
  linksQuery,
  mapForArtifact,
  mapForHasItemId,
  transcriptionsQuery,
} from "./caching";
import { clampToContiguousHistory } from "./feedContiguity";
import { CurrentFeedContext } from "./StateProviders/currentFeedProvider";
import { MyAccountContext } from "./StateProviders/myAccountProvider";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";

type FeedStatusMessage = {
  message: string;
  severity: "error" | "success" | "info";
};

export interface FullItem {
  id: string;
  feedId: string;
  contentId: string;
  groupId?: string;
  deletedAt?: string;
  createdAt: string;
  accountId: string;
  status: string;
  mine: boolean;
  label: string;
  key: string;
  feedItemIndex: number;
  lastActiveItem: boolean;
  lastActiveItemIdIsMine: boolean;
  shortDateStr: string;
  loadedContent: boolean;
  url?: string;
  isSilent?: boolean;
  unread?: boolean;
  transcriptions: Transcription[];
  audioEncodings: AudioEncoding[];
  files: File[];
  links: Link[];
  displayArtifact: DisplayArtifact;
  callRecords: CallRecord[];
  fromAppsync?: boolean;
  hasRegisteredSeenEvent?: boolean;
  clientRecordedAt?: string | null;
  clientSentAt?: string | null;
  serverReceivedAt?: string | null;

  account?: AccountWithWsMembershipAvailability;
  events: AccountEvent[];
}

type FeedState = {
  vListRef?: MutableRefObject<VirtualListHandle>;
  fileUploadModal?: boolean;
  status?: FeedStatusMessage;
  templatesModal?: boolean;
  itemTranscriptState?: string[];
  setItemTranscriptState?: SetStateType<string[]>;
  itemMessageDetailsState?: Record<string, boolean>;
  scrollToBottomOfFeed?: () => void;
  setFileUploadModal: SetStateType<boolean>;
  setItemMessageDetailsState?: SetStateType<Record<string, boolean>>;
  setTemplatesModal: SetStateType<boolean>;
  setStatus: SetStateType<FeedStatusMessage>;
  items?: Item[];
  feedMap?: FullItem[];
  /** `feedMap` with the driver's TMS event chips interleaved; what the list renders. Item rows only while chips are off. */
  feedRows?: FeedRow[];
  currentFeed?: Feed;
  windowFullyLoaded?: boolean;
  expandFeedWindow: () => void;
  setFeedWindowStartTimestamp: (timestamp: string) => void;
};

export const FeedContext = createContext<FeedState>({
  scrollToBottomOfFeed: () => {},
  setFileUploadModal: () => {},
  setTemplatesModal: () => {},
  setItemMessageDetailsState: () => {},
  setStatus: () => {},
  expandFeedWindow: () => {},
  setFeedWindowStartTimestamp: () => {},
});

// Items per "page" of the local-DB query window. Each call to
// `expandFeedWindow()` walks the anchor back by another page worth of
// items, bounding every feed-content query to that window. This caps how
// many items (and their per-item artifacts and events) each query
// materializes, so it's the single biggest cost knob on the channel load.
// 50 prioritises a fast cold load — most channel visits never scroll back
// — and keeps a ~40-item scroll buffer past the viewport before the next
// expansion fires. See WEB-1985 and WEB-2149. Exported because the
// cold-open skip check (WEB-2254) honors `loadedLastPage` only when this
// many local items back it.
export const FEED_WINDOW_PAGE_SIZE = 50;

/**
 * The whole-row `select(({ artifact }) => artifact)` queries return the artifact rows unchanged at
 * runtime, but @tanstack/db 0.6.17 fails to infer the result type of a bare-ref select; this pins
 * each result back to its row type.
 */
function artifactRows<T>(data: readonly unknown[] | undefined): T[] {
  return (data ?? []) as T[];
}

const FeedContextProvider = ({ children }) => {
  // context use to persist state in the feed virtualized list until refactor occurs
  const [templatesModal, setTemplatesModal] = useState<boolean>(false);
  const [fileUploadModal, setFileUploadModal] = useState<boolean>(false);
  const [itemTranscriptState, setItemTranscriptState] = useState<string[]>([]);
  const [itemMessageDetailsState, setItemMessageDetailsState] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<FeedStatusMessage>(null);
  const vListRef = useRef<VirtualListHandle>(null);

  const scrollToBottomOfFeed = () => {
    if (vListRef?.current) {
      setTimeout(() => {
        vListRef.current?.scrollToOffset(vListRef.current?.scrollSize);
      }, 500);
    }
  };
  const { myAccount } = useContext(MyAccountContext);
  const { currentFeedId, currentFeed } = useContext(CurrentFeedContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const accountOf = useAccountLookup();
  const { preferredLanguage } = useContext(DataContext);
  const dispatchEnabled = useDispatchEnabled(currentWorkspaceId);

  // Local-DB pagination window. The anchor is the createdAt of the
  // (pageCount * FEED_WINDOW_PAGE_SIZE)th newest non-deleted item; every
  // feed-content query below is bounded to
  // `item.createdAt >= windowStartTimestamp`. The live tail is unbounded
  // on the upper side so newly-arrived messages flow in via the existing
  // table-event subscription. Without windowing, each query scales by
  // `total_items * per_item_fanout` on busy
  // long-lived channels — see WEB-1985.
  const [windowPageCount, setWindowPageCount] = useState(1);
  const [windowStartTimestamp, setWindowStartTimestamp] = useState<string | null>(null);
  const [windowFullyLoaded, setWindowFullyLoaded] = useState(false);
  // Tracks whether the probe has resolved at least once for the current
  // feed. Until it has, we hold back the content query so we never paint
  // an unbounded result first and then re-shrink it once the probe lands
  // (which produced a visible flash of the "Jump to latest" chip on
  // channel switches with lots of cached history).
  const [windowReadyForFeedId, setWindowReadyForFeedId] = useState<string | null>(null);

  // Oldest deeplink-pushed anchor for the current feed. Kept outside the window state so a probe
  // that later re-windows the feed (after deeplink fetches grow it past the page size) can clamp
  // its anchor and never exclude the deeplinked item. Mirrored into state because the contiguity
  // clamp below has to loosen for it, and a ref alone would not re-render.
  const [deeplinkFloor, setDeeplinkFloor] = useState<string | null>(null);
  const deeplinkFloorRef = useRef<string | null>(null);

  // Reset the window whenever the user switches channels, otherwise the
  // previous feed's anchor would briefly leak into the new feed's query.
  useEffect(() => {
    setWindowPageCount(1);
    setWindowStartTimestamp(null);
    setWindowFullyLoaded(false);
    setWindowReadyForFeedId(null);
    setDeeplinkFloor(null);
    deeplinkFloorRef.current = null;
  }, [currentFeedId]);

  const expandFeedWindow = useCallback(() => {
    if (windowFullyLoaded) return;
    setWindowPageCount((n) => n + 1);
  }, [windowFullyLoaded]);

  // Read through a ref so the deeplink callback below never closes over a stale flag — the
  // deeplink flow calls it after multi-second API fetches, long after the probe may have resolved.
  const windowFullyLoadedRef = useRef(windowFullyLoaded);
  windowFullyLoadedRef.current = windowFullyLoaded;

  // Allow callers (e.g. deeplinked search-result navigation) to push the
  // window anchor older than the probe-derived default so the deeplinked
  // item is guaranteed to be included. Same monotonically-expand-only
  // rule as the probe — never lets the window contract.
  const setFeedWindowStartTimestamp = useCallback((timestamp: string) => {
    // Always remember the push: if the deeplink fetches grow the table past the page size, the
    // probe will re-window the feed, and its anchor must be clamped to this floor or the
    // deeplinked item could fall outside the window with no way back (expandFeedWindow only
    // walks in page-sized steps behind scroll events).
    const prevFloor = deeplinkFloorRef.current;
    if (prevFloor === null || new Date(timestamp).getTime() < new Date(prevFloor).getTime()) {
      deeplinkFloorRef.current = timestamp;
      setDeeplinkFloor(timestamp);
    }
    // A fully-loaded window already covers every local row (unbounded); pinning it to the
    // deeplinked item would hide the rows older than the deeplink instead of guaranteeing
    // the deeplink is visible.
    if (windowFullyLoadedRef.current) return;
    setWindowStartTimestamp((prev) => {
      if (prev === null) return timestamp;
      return new Date(timestamp).getTime() < new Date(prev).getTime() ? timestamp : prev;
    });
  }, []);

  const anchorOffset = windowPageCount * FEED_WINDOW_PAGE_SIZE - 1;
  const { data: anchorData, isReady: anchorReady } = useLiveQuery((q) =>
    currentFeedId ? feedWindowAnchorQuery(currentFeedId, anchorOffset)(q) : null,
  );
  const anchorRows = anchorData ?? [];

  // The probe returns no rows when the window already covers every item
  // in the feed. Mark the window as fully loaded in that case so
  // expandFeedWindow becomes a no-op, but always recompute on every probe
  // result — channels can grow past the threshold over time as new
  // messages arrive or older API pages land. Always mark windowReady so
  // the gated content query unblocks (regardless of whether the probe
  // returned a row or not).
  useEffect(() => {
    // An un-ready probe is indistinguishable from a zero-row one by its data alone; acting on it
    // would unblock the content queries unbounded and re-shrink them when the probe lands (the
    // WEB-1985 "long → short" flash). Wait for the live query's explicit ready signal.
    if (!currentFeedId || !anchorReady) return;
    const probedAnchor = anchorRows[0]?.createdAt ?? null;
    setWindowReadyForFeedId(currentFeedId);
    if (probedAnchor === null) {
      setWindowFullyLoaded(true);
      // Fewer local rows than the window size, so an unbounded query is cheap. Drop any
      // deeplink-pushed anchor: leaving it in place would permanently filter out rows older
      // than the deeplinked item (they exist locally but fall outside the window, and
      // expandFeedWindow is a no-op once the window is fully loaded).
      setWindowStartTimestamp(null);
      return;
    }
    setWindowFullyLoaded(false);
    // Clamp to any deeplink floor recorded while the window was fully loaded — the deeplink's
    // own push was skipped then, and without the clamp this anchor could window out the
    // deeplinked item.
    const floor = deeplinkFloorRef.current;
    const anchor =
      floor !== null && new Date(floor).getTime() < new Date(probedAnchor).getTime() ? floor : probedAnchor;
    setWindowStartTimestamp((prev) => {
      if (prev === null) return anchor;
      // Monotonically expand-only: ignore probes that walked forward
      // because new live-tail messages bumped the OFFSET row, and never
      // let the window contract.
      return new Date(anchor).getTime() < new Date(prev).getTime() ? anchor : prev;
    });
  }, [anchorRows, anchorReady, currentFeedId]);

  const windowReady = windowReadyForFeedId === currentFeedId;

  // Each content type is fetched in its own contentId/itemId-keyed query
  // rather than a single flat multi-LEFT-JOIN. A combined query keys every
  // join back to `item`, so its row count is the cartesian product of all
  // per-item matches — for a channel with high account_event and audio
  // fan-out that reached ~160k rows each carrying a full transcript,
  // exhausting the old in-browser SQL worker and rendering the channel
  // empty (see WEB-2149). Splitting the joins means no product ever forms:
  // each artifact row appears exactly once.
  //
  // Every query is bounded to the same pagination window and held back via
  // `windowReady` until the probe has resolved for this feed. Otherwise an
  // unbounded run can populate `feedMap` with the channel's full local
  // history first, then the probe lands, the queries re-run windowed, and
  // `feedMap` shrinks — the resulting "long → short" transition flashed the
  // `Jump to latest` chip on channel switches with lots of cached history.
  const gatedFeedId = windowReady ? currentFeedId : undefined;
  const windowBound =
    clampToContiguousHistory({
      windowStartTimestamp,
      contiguityFloor: currentFeed?.oldestContiguousCreatedAt,
      deeplinkFloor,
      loadedFirstPage: currentFeed?.loadedFirstPage,
    }) ?? undefined;

  const { data: itemRows } = useLiveQuery((q) => (gatedFeedId ? itemsQuery(gatedFeedId, windowBound)(q) : null));
  const items: Item[] = itemRows ?? [];

  const { data: transcriptionRows } = useLiveQuery((q) =>
    gatedFeedId && preferredLanguage ? transcriptionsQuery(gatedFeedId, preferredLanguage, windowBound)(q) : null,
  );
  const transcriptions = artifactRows<Transcription>(transcriptionRows);

  const { data: audioEncodingRows } = useLiveQuery((q) =>
    gatedFeedId ? audioEncodingsQuery(gatedFeedId, windowBound)(q) : null,
  );
  const audioEncodings = artifactRows<AudioEncoding>(audioEncodingRows);

  const { data: displayArtifactRows } = useLiveQuery((q) =>
    gatedFeedId ? displayArtifactsQuery(gatedFeedId, windowBound)(q) : null,
  );
  const displayArtifacts = artifactRows<DisplayArtifact>(displayArtifactRows);

  const { data: linkRows } = useLiveQuery((q) => (gatedFeedId ? linksQuery(gatedFeedId, windowBound)(q) : null));
  const links = artifactRows<Link>(linkRows);

  const { data: fileRows } = useLiveQuery((q) => (gatedFeedId ? filesQuery(gatedFeedId, windowBound)(q) : null));
  const files = artifactRows<File>(fileRows);

  const { data: eventRows } = useLiveQuery((q) =>
    gatedFeedId ? eventsForFeedItemsQuery(gatedFeedId, windowBound)(q) : null,
  );
  const events = artifactRows<AccountEvent>(eventRows);

  const { data: callRecordRows } = useLiveQuery((q) =>
    gatedFeedId ? callRecordsQuery(gatedFeedId, windowBound)(q) : null,
  );
  const callRecords = artifactRows<CallRecord>(callRecordRows);

  // Create maps for efficient lookups
  const transcriptionMap = useMemo(() => mapForArtifact(transcriptions), [transcriptions]);
  const audioEncodingMap = useMemo(() => mapForArtifact(audioEncodings), [audioEncodings]);
  const displayArtifactMap = useMemo(() => mapForArtifact(displayArtifacts), [displayArtifacts]);
  const linksMap = useMemo(() => mapForArtifact(links), [links]);
  const filesMap = useMemo(() => mapForArtifact(files), [files]);
  const eventsMap = useMemo(() => mapForHasItemId(events), [events]);
  const callRecordsMap = useMemo(() => mapForArtifact(callRecords), [callRecords]);

  // Date headers are not decided here: `interleaveFeedRows` opens each day with its own row over the merged sequence
  // of messages and chips, so a header never rides on an item whose height the list has already measured.
  const feedMap = useMemo(() => {
    const ret =
      items?.map((feedItem, index): FullItem => {
        const { shortDateStr, formattedDate: dailyLabel, formattedTime } = formatItemDate(feedItem.createdAt);

        const lastActiveItem = items[items.length - 1]?.id === feedItem.id;

        const lastActiveItemIdIsMine = lastActiveItem && feedItem?.accountId === myAccount?.id;

        const mySeenEventForItem = eventsMap
          .get(feedItem.id)
          ?.find((event) => event.name === "Saw Feed Item" && event.accountId === myAccount?.id);
        return {
          ...feedItem,
          id: feedItem.id,
          feedId: feedItem.feedId,
          contentId: feedItem.contentId,
          groupId: feedItem.groupId,
          deletedAt: feedItem.deletedAt,
          createdAt: feedItem.createdAt,
          accountId: feedItem.accountId,
          status: feedItem.status,
          mine: feedItem.accountId === myAccount?.id,
          unread: feedItem.unread,
          label: `${dailyLabel} @ ${formattedTime}`,
          key: feedItem.createdAt,
          feedItemIndex: index++,
          lastActiveItem,
          lastActiveItemIdIsMine,
          shortDateStr,
          loadedContent: feedItem.loadedContent,
          url: `${window?.location?.href?.split("#")[0]}#${feedItem?.id}` || "",
          transcriptions: transcriptionMap.get(feedItem.contentId) || [],
          audioEncodings: audioEncodingMap.get(feedItem.contentId) || [],
          files: filesMap.get(feedItem.contentId) || [],
          displayArtifact: displayArtifactMap.get(feedItem.contentId)?.[0],
          links: linksMap.get(feedItem.contentId) || [],
          callRecords: callRecordsMap.get(feedItem.contentId) || [],
          fromAppsync: feedItem.fromAppSync,
          hasRegisteredSeenEvent: feedItem.hasRegisteredSeenEvent || !!mySeenEventForItem,

          account: accountOf(feedItem.accountId),
          events: eventsMap.get(feedItem.id) || [],
        };
      }) || [];
    return ret;
  }, [
    items,
    myAccount,
    transcriptionMap,
    audioEncodingMap,
    filesMap,
    linksMap,
    displayArtifactMap,
    callRecordsMap,
    eventsMap,
    accountOf,
    currentFeedId,
  ]);

  // The driver's TMS events, only for an alias feed and only in a workspace on the Dispatch page; group and DM feeds
  // show none.
  const eventMembershipId = feedEventMembershipId(currentFeed, dispatchEnabled);
  const { data: tmsEventRows } = useLiveQuery((q) =>
    eventMembershipId ? tmsEventsForMembershipQuery(eventMembershipId)(q) : null,
  );
  const tmsEvents = artifactRows<TmsEventRow>(tmsEventRows);

  // The span's floor is the rendered window's oldest message, so a chip never sits above a message not yet on screen;
  // expanding the window brings older chips in with older messages. Once the feed has no older history to fetch and
  // the window holds all of it, there is no hole above the window and no floor. Without either, and with nothing
  // rendered yet, there is nothing to hold events to, so none show. `newest` is the server's now: events are
  // server-stamped, so a browser clock running behind must not hide a fresh one.
  const historyExhausted = currentFeed?.loadedFirstPage === true && windowFullyLoaded;
  const feedRows = useMemo(() => {
    const oldest = historyExhausted ? null : feedMap[0]?.createdAt;
    const events = eventMembershipId && oldest !== undefined ? tmsEvents : [];
    return interleaveFeedRows(feedMap, events, { oldest: oldest ?? null, newest: serverNow().toISOString() });
  }, [feedMap, tmsEvents, eventMembershipId, historyExhausted]);

  useEffect(() => {
    log.info("FeedContextProvider: MOUNTED");
    return () => {
      log.info("FeedContextProvider: UNMOUNTED");
    };
  }, []);

  const feedState: FeedState = {
    vListRef,
    fileUploadModal,
    status,
    templatesModal,
    itemTranscriptState,
    setItemTranscriptState,
    itemMessageDetailsState,
    scrollToBottomOfFeed,
    setItemMessageDetailsState,
    setFileUploadModal,
    setTemplatesModal,
    setStatus,
    // items,
    feedMap,
    feedRows,
    currentFeed,
    windowFullyLoaded,
    expandFeedWindow,
    setFeedWindowStartTimestamp,
  };
  return <FeedContext.Provider value={feedState}>{children}</FeedContext.Provider>;
};

export default FeedContextProvider;
